import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

/**
 * The Monday (00:00) of the Monday–Sunday tracking week that contains `date`
 * (defaults to today). Falsy or unparseable input (e.g. an empty filter param)
 * also falls back to today rather than producing an "Invalid Date".
 */
export function mondayOf(date: dayjs.Dayjs | string | Date = dayjs()): dayjs.Dayjs {
  const parsed = dayjs(date);
  return (parsed.isValid() ? parsed : dayjs()).startOf('isoWeek');
}

const WEEKEND_DAYS = [0, 6]; // Sunday, Saturday

/**
 * The first working day strictly after `date`, rolling Saturday and Sunday forward to Monday.
 * Turns an inclusive last-day-of-work into the exclusive boundary where free time begins.
 */
export function nextWorkingDay(date: Date): Date {
  const next = new Date(date);
  do {
    // setDate (rather than adding 24h of milliseconds) keeps the local time-of-day across a DST shift
    next.setDate(next.getDate() + 1);
  } while (WEEKEND_DAYS.includes(next.getDay()));
  return next;
}

/** The last working day strictly before `date`; the inverse of {@link nextWorkingDay}. */
export function previousWorkingDay(date: Date): Date {
  const previous = new Date(date);
  do {
    previous.setDate(previous.getDate() - 1);
  } while (WEEKEND_DAYS.includes(previous.getDay()));
  return previous;
}

/**
 * The number of working days in the half-open range [startInclusive, endExclusive), 0 when the
 * range is empty or inverted. This is the unit availability is actually measured in: a week-long
 * calendar gap can hold anywhere from four to five working days depending on the day it opens on.
 */
export function workingDaysBetween(startInclusive: Date, endExclusive: Date): number {
  let count = 0;
  const cursor = new Date(startInclusive);
  while (cursor < endExclusive) {
    if (!WEEKEND_DAYS.includes(cursor.getDay())) {
      count++;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

/**
 * Min consecutive *working* days of free time before the Planner canvas draws an availability bar.
 * Counted in working days because that is the unit the availability boundary itself works in; 4 is
 * the value that keeps the rendering this threshold had when it counted 7 calendar days from a
 * period that opened a day early; 5 would blank windows that used to draw. It lives here rather
 * than beside the canvas so the calibration that pins it can import the value the canvas uses.
 */
export const MIN_AVAILABILITY_WORKING_DAYS = 4;

/**
 * Checks if a half-open date period [startDate, endDate) contains no working day
 * - It's 3 days + 1 hour or less (to account for DST transitions)
 * - It starts on Saturday or Sunday
 * - It ends on Saturday, Sunday, or Monday
 *
 * A Friday start is not weekend-only: the Friday itself is still free. Passing an inclusive end
 * date instead of an exclusive one therefore hides a genuinely free Friday.
 *
 * @param startDate - The start date of the period
 * @param endDate - The day the period ends, exclusive (can be null for open-ended periods)
 * @returns true if the period is just a weekend, false otherwise
 */
export function isWeekendPeriod(startDate: Date, endDate: Date | null): boolean {
  if (!endDate) {
    return false;
  }

  // Use 3 days + 1 hour grace period to account for DST transitions
  // DST changes typically add or subtract exactly 1 hour
  const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
  const oneHourInMs = 60 * 60 * 1000;
  const maxWeekendDuration = threeDaysInMs + oneHourInMs;

  if (endDate.getTime() - startDate.getTime() > maxWeekendDuration) {
    return false;
  }

  const weekendEndDays = [0, 1, 6]; // Sunday, Monday, Saturday

  return WEEKEND_DAYS.includes(startDate.getDay()) && weekendEndDays.includes(endDate.getDay());
}
